let sharedAudioContext: AudioContext | null = null;
let isAudioUnlocked = false;
let unlockListenersInstalled = false;
let hasUserInteracted = false;

function getAudioContextConstructor(): typeof AudioContext | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ?? null;
}

function getSharedAudioContext(): AudioContext | null {
  if (sharedAudioContext) {
    return sharedAudioContext;
  }

  const AudioContextConstructor = getAudioContextConstructor();
  if (!AudioContextConstructor) {
    return null;
  }

  sharedAudioContext = new AudioContextConstructor();
  return sharedAudioContext;
}

export async function unlockRealtimeSounds(): Promise<boolean> {
  hasUserInteracted = true;

  const context = getSharedAudioContext();
  if (!context) {
    return false;
  }

  try {
    if (context.state === 'suspended') {
      await context.resume();
    }

    isAudioUnlocked = context.state === 'running';
    return isAudioUnlocked;
  } catch {
    isAudioUnlocked = false;
    return false;
  }
}

export function installRealtimeSoundUnlock(): void {
  if (typeof window === 'undefined' || unlockListenersInstalled) {
    return;
  }

  unlockListenersInstalled = true;

  const tryUnlock = () => {
    hasUserInteracted = true;
    void unlockRealtimeSounds().finally(() => {
      if (!isAudioUnlocked) {
        return;
      }

      cleanup();
    });
  };

  const cleanup = () => {
    window.removeEventListener('pointerdown', tryUnlock);
    window.removeEventListener('touchstart', tryUnlock);
    window.removeEventListener('keydown', tryUnlock);
  };

  window.addEventListener('pointerdown', tryUnlock, { passive: true });
  window.addEventListener('touchstart', tryUnlock, { passive: true });
  window.addEventListener('keydown', tryUnlock, { passive: true });
}

async function playToneSequence(tones: Array<{ frequency: number; durationMs: number; gain: number }>): Promise<boolean> {
  if (!hasUserInteracted || !isAudioUnlocked) {
    return false;
  }

  const context = getSharedAudioContext();
  if (!context) {
    return false;
  }

  try {
    if (context.state !== 'running') {
      const unlocked = await unlockRealtimeSounds();
      if (!unlocked) {
        return false;
      }
    }

    const startAt = context.currentTime;
    let cursor = startAt;

    for (const tone of tones) {
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = tone.frequency;

      gainNode.gain.setValueAtTime(0.0001, cursor);
      gainNode.gain.exponentialRampToValueAtTime(tone.gain, cursor + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, cursor + tone.durationMs / 1000);

      oscillator.connect(gainNode);
      gainNode.connect(context.destination);

      oscillator.start(cursor);
      oscillator.stop(cursor + tone.durationMs / 1000 + 0.02);

      cursor += tone.durationMs / 1000 + 0.04;
    }

    const totalDurationMs = tones.reduce((acc, tone) => acc + tone.durationMs, 0) + Math.max(tones.length - 1, 0) * 40;

    await new Promise((resolve) => {
      window.setTimeout(resolve, totalDurationMs + 120);
    });
    return true;
  } catch {
    return false;
  }
}

export async function playRealtimeConnectedSound(): Promise<void> {
  await playToneSequence([
    { frequency: 740, durationMs: 110, gain: 0.03 },
    { frequency: 988, durationMs: 150, gain: 0.04 },
  ]);
}

export async function playRealtimeNotificationSound(): Promise<void> {
  await playToneSequence([
    { frequency: 660, durationMs: 90, gain: 0.028 },
    { frequency: 880, durationMs: 130, gain: 0.035 },
  ]);
}
